<template>
  <Teleport to="body">
    <div
      class="absolute top-0 left-0 h-screen w-screen overflow-hidden bg-black/50 transition-opacity duration-500 flex z-10"
      :class="{ 'pointer-events-none !bg-black/0': hasAccepted }"
    >
      <Transition
        mode="out-in"
        enter-active-class="transition duration-1000 transform"
        enter-from-class="opacity-0 translate-y-full"
        leave-active-class="duration-1000 transform"
        leave-to-class="opacity-0 translate-y-full"
        appear
      >
        <div
          v-if="!hasAccepted"
          data-cookies-container
          class="box-border self-end z-10 m-6 max-[380px]:max-h-[250px] max-h-[224px] max-w-[364px] h-full w-full rounded-md bg-[#FDFDFD] p-6 text-[#666666] dark:bg-[#1D1D1D] dark:text-[#D2D2D2]"
        >
          <div class="mb-4 font-semibold text-[#212121] dark:text-[#666666]">{{ $t('mainContent.cookies.title') }}</div>

          <div class="mb-4 text-sm leading-4">
            {{ $t('mainContent.cookies.content') }}
            <a href="#" @click="showPrivacyPolicy = true" class="font-semibold">{{ $t('labels.privacyPolicy') }}</a>. {{ $t('mainContent.cookies.continue') }}
          </div>

          <div>
            <UiButton type="primary" class="w-full p-4" @click="acceptCookies" tabindex="0">
              {{ $t('button.accept') }}
            </UiButton>
          </div>
        </div>
      </Transition>

      <Transition
        mode="out-in"
        enter-active-class="transition duration-300 transform"
        enter-from-class="opacity-0 -translate-y-8"
        leave-active-class="duration-300 transform"
        leave-to-class="opacity-0 -translate-y-8"
        appear
      >
        <div v-if="showPrivacyPolicy" data-cookies-container class="absolute top-0 left-0 z-10 box-border h-full w-full p-4">
          <div class="box-border flex h-full w-full flex-col rounded-md bg-neutral-900 p-4">
            <div class="py-4">
              <a
                href="#"
                class="border-0 border-b-2 border-solid border-transparent py-2 pr-1 !text-neutral-50 no-underline transition-all hover:border-neutral-50"
                @click="showPrivacyPolicy = false"
              >
                <UiIcon icon="arrow-left" />{{ $t('button.back') }}
              </a>
            </div>
            <iframe
              :src="privacyPolicyLink"
              frameborder="0"
              class="h-full w-full"
            />
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { cookies } from '@/common/cookies'

import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'

import { useSystemStore } from '@/stores'

const systemStore = useSystemStore()

const hasAccepted = ref(cookies.get<boolean>('privacy-policy') || false)
const showPrivacyPolicy = ref(false)

const privacyPolicyLink = computed(() => 
  systemStore.clientSettings.settings.general.privacyPolicyUrl || 'https://www.plantareducacao.com.br/politica-de-privacidade/'
)

const acceptCookies = () => {
  cookies.add('privacy-policy', true)
  hasAccepted.value = true
}

// Função de capturar "foco" com Tab e Shift+Tab
const handleKeyDown = (event: KeyboardEvent) => {
  if (hasAccepted.value) return
  
  if (event.key === 'Tab') {
    const focusableElements = document.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    
    const cookiesContainer = document.querySelector('[data-cookies-container]')
    if (!cookiesContainer) return
    
    const visibleFocusableElements = Array.from(focusableElements).filter(el => 
      cookiesContainer.contains(el) && 
      (el as HTMLElement).offsetParent !== null
    ) as HTMLElement[]
    
    if (visibleFocusableElements.length === 0) return
    
    const firstElement = visibleFocusableElements[0]
    const lastElement = visibleFocusableElements[visibleFocusableElements.length - 1]
    
    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
  if (!hasAccepted.value) {
    setTimeout(() => {
      const anchor = document.querySelector('[data-cookies-container] a[href]')
      if (anchor) (anchor as HTMLElement).focus()
    }, 0)
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>
